import React, { useState, useEffect, useRef } from 'react'; // Убрали useCallback
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  TextField,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  Paper,
  InputAdornment,
} from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { AppDispatch, RootState } from '../../store';
import { searchGames, clearSearchResults } from '../../store/gamesSlice';
import { addToFavorites, removeFromFavorites } from '../../store/favoritesSlice';
import GameCard from '../../components/GameCard/GameCard';
import { Game } from '../../types/game';
import { debounce } from '@mui/material/utils';

const Search: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  const { searchResults, status, error } = useSelector((state: RootState) => state.games);
  const favorites = useSelector((state: RootState) => state.favorites.items);

  const debouncedSearchRef = useRef(
    debounce((term: string) => {
      if (term.trim()) {
        setIsSearching(true);
        dispatch(searchGames(term)).finally(() => setIsSearching(false));
      } else {
        dispatch(clearSearchResults());
      }
    }, 500)
  );

  useEffect(() => {
    const debouncedSearch = debouncedSearchRef.current;
    return () => {
      debouncedSearch.clear();
    };
  }, []);

  useEffect(() => {
    return () => {
      dispatch(clearSearchResults());
    };
  }, [dispatch]);

  useEffect(() => {
    debouncedSearchRef.current(searchTerm);
  }, [searchTerm]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleFavoriteToggle = (game: Game) => {
    const isFavorite = favorites.some(fav => fav.id === game.id);
    if (isFavorite) {
      dispatch(removeFromFavorites(game.id));
    } else {
      dispatch(addToFavorites(game));
    }
  };

  const handleGameClick = (gameId: number) => {
    navigate(`/game/${gameId}`);
  };

  const displayedGames = searchTerm ? searchResults : [];

  return (
    <div>
      <Typography variant="h4" component="h1" gutterBottom>
        Поиск игр
      </Typography>

      <Paper elevation={2} style={{ padding: '2rem', marginBottom: '2rem' }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Введите название игры или описание..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {isSearching ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '2rem' }}>
          <CircularProgress />
        </div>
      ) : status === 'failed' ? (
        <Alert severity="error">Ошибка поиска: {error}</Alert>
      ) : (
        <>
          {searchTerm && (
            <Typography variant="body1" paragraph color="textSecondary">
              Найдено игр: {displayedGames.length}
            </Typography>
          )}
          
          {searchTerm && displayedGames.length > 0 && (
            <Grid container spacing={3}>
              {displayedGames.map((game) => (
                <Grid item xs={12} sm={6} md={4} key={game.id}>
                  <GameCard
                    game={game}
                    isFavorite={favorites.some(fav => fav.id === game.id)}
                    onFavoriteToggle={handleFavoriteToggle}
                    onClick={() => handleGameClick(game.id)}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {searchTerm && displayedGames.length === 0 && !isSearching && (
            <Alert severity="info">По вашему запросу ничего не найдено</Alert>
          )}

          {!searchTerm && (
            <Typography variant="body1" color="textSecondary" align="center">
              Введите текст для поиска игр
            </Typography>
          )}
        </>
      )}
    </div>
  );
};

export default Search;